type BankEntryInfoDelegate = BankInfoLinkCellDelegate & BankInfoAnalyticAccountCellDelegate & BankMovementLinkCellDelegate & BankMovementDifferenceCellDelegate & BankInfoTagsCellDelegate & BankInfoSimilarCellDelegate;

// The detail panel of a movement, a section per block: the header (status bar, date, amount,
// concept and the bank's fields), the analytic account, the links with the difference they leave,
// the tags and similar movements
class BankEntryInfoDatasource extends MIOObject
{
    private static readonly HeaderSection = 0;
    private static readonly AnalyticAccountSection = 1;
    private static readonly LinksSection = 2;
    private static readonly TagsSection = 3;
    private static readonly SimilarSection = 4;

    private tableView:UITableView = null;
    delegate:BankEntryInfoDelegate = null;
    suggester:AnalyticAccountSuggester = null;
    availableTags:string[] = [];

    initWithTableView(tableView:UITableView){
        this.tableView = tableView;
        this.tableView.dataSource = this;
        this.tableView.delegate = this as any;
    }

    private linkSummary:BankMovementLinkSummary = { links: [], total: 0, mismatch: false, remaining: 0 };
    private similar:BankMovement[] = [];
    private _entry:BankEntry = null;

    get entry() : BankEntry { return this._entry; }

    setEntry(item:BankEntry, similar:BankMovement[]) {
        this._entry = item;
        this.similar = similar ?? [];

        this.linkSummary = item instanceof BankMovement
            ? BankMovementState.linkSummary(item as BankMovement)
            : { links: [], total: 0, mismatch: false, remaining: 0 };

        this.tableView.reloadData();
    }

    numberOfSections(tableView:UITableView){ return this._entry == null ? 0 : 5; }

    numberOfRowsInSection(tableView:UITableView, section:number){
        switch (section) {
            case BankEntryInfoDatasource.HeaderSection: return 1;
            case BankEntryInfoDatasource.AnalyticAccountSection: return 1;
            // Title row + links + the difference row when the links don't cover the movement amount
            case BankEntryInfoDatasource.LinksSection: return 1 + this.linkSummary.links.length + (this.linkSummary.mismatch ? 1 : 0);
            case BankEntryInfoDatasource.TagsSection: return 1;
            case BankEntryInfoDatasource.SimilarSection: return this.similar.length > 0 ? 1 : 0;
        }
        return 0;
    }

    cellAtIndexPath(tableview:UITableView, indexPath:MIOIndexPath){
        switch (indexPath.section) {
            case BankEntryInfoDatasource.HeaderSection: {
                let cell = tableview.dequeueReusableCellWithIdentifier("BankMovementHeaderCell") as BankMovementHeaderCell;
                cell.setEntry(this._entry);
                return cell;
            }

            case BankEntryInfoDatasource.AnalyticAccountSection: {
                let cell = tableview.dequeueReusableCellWithIdentifier("BankInfoAnalyticAccountCell") as BankInfoAnalyticAccountCell;
                let suggestion:AnalyticAccountSuggestion = null;
                if (this.suggester != null && this._entry instanceof BankMovement && BankMovementState.isCategorized(this._entry) == false) {
                    suggestion = this.suggester.suggestionFor(this._entry as BankMovement);
                }
                cell.setEntry(this._entry, suggestion);
                cell.delegate = this.delegate;
                return cell;
            }

            case BankEntryInfoDatasource.LinksSection: {
                let cell = this.linkCellAtRow(tableview, indexPath.row);
                // the last row closes the links card
                let last = indexPath.row == this.numberOfRowsInSection(tableview, indexPath.section) - 1;
                if (last) MUICoreLayerAddStyle(cell.layer, "info-group-last");
                else MUICoreLayerRemoveStyle(cell.layer, "info-group-last");
                return cell;
            }

            case BankEntryInfoDatasource.TagsSection: {
                let cell = tableview.dequeueReusableCellWithIdentifier("BankInfoTagsCell") as BankInfoTagsCell;
                cell.setEntry(this._entry, this.availableTags);
                cell.delegate = this.delegate;
                return cell;
            }

            default: {
                let cell = tableview.dequeueReusableCellWithIdentifier("BankInfoSimilarCell") as BankInfoSimilarCell;
                cell.setItem(this._entry as BankMovement, this.similar);
                cell.delegate = this.delegate;
                return cell;
            }
        }
    }

    private linkCellAtRow(tableview:UITableView, row:number) : UITableViewCell {
        // Row 0 is the title with the add button, the links follow
        if (row == 0) {
            let cell = tableview.dequeueReusableCellWithIdentifier("BankInfoLinkCell") as BankInfoLinkCell;
            cell.entry = this._entry;
            cell.delegate = this.delegate;
            return cell;
        }

        // Last row when there is a mismatch: the pending difference with the add button
        if (this.linkSummary.mismatch && row == this.linkSummary.links.length + 1) {
            let cell = tableview.dequeueReusableCellWithIdentifier("BankMovementDifferenceCell") as BankMovementDifferenceCell;
            cell.setItem(this._entry, this.linkSummary.remaining);
            cell.delegate = this.delegate;
            return cell;
        }

        let cell = tableview.dequeueReusableCellWithIdentifier("BankMovementLinkCell") as BankMovementLinkCell;
        cell.setItem(this.linkSummary.links[row - 1]);
        cell.delegate = this.delegate;
        return cell;
    }

    canSelectCellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath){ return false; }
}
